import React from 'react';
import {
  List,
  ListSubheader,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  ButtonGroup,
  Button,
} from '@mui/material';
import { format, isToday, isTomorrow, addDays, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AccessTime as TimeIcon,
  ChevronRight as ChevronRightIcon,
  Group as GroupIcon,
  ChevronLeft as ChevronLeftIcon,
  Today as TodayIcon,
} from '@mui/icons-material';

const MobileActivityList = ({ 
  activities,
  currentDate,
  onDateChange
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handlePrevious = () => {
    onDateChange(subDays(currentDate, 1));
  };

  const handleNext = () => {
    onDateChange(addDays(currentDate, 1));
  };

  const handleToday = () => {
    onDateChange(new Date());
  };

  const getDateHeader = (date) => {
    if (isToday(date)) {
      return t('today');
    }
    if (isTomorrow(date)) {
      return t('tomorrow');
    }
    return format(date, 'EEEE d MMMM', { locale: fr });
  };

  const getActivityStatusChip = (activity) => {
    const spotsLeft = activity.maxParticipants - activity.currentParticipants;
    const isFull = spotsLeft === 0;
    
    if (activity.category === 'Shiatsu' || activity.category === 'Chi Nei Tsang') {
      return (
        <Chip
          size="small"
          label={isFull ? '1/1' : '0/1'}
          color={isFull ? 'error' : 'success'}
          sx={{ minWidth: 60 }}
        />
      );
    }

    return (
      <Chip
        size="small"
        label={`${activity.currentParticipants}/${activity.maxParticipants}`}
        color={isFull ? 'error' : 'success'}
        sx={{ minWidth: 60 }}
      />
    );
  };

  // Navigation header
  const NavigationHeader = () => (
    <Box sx={{ 
      p: 2, 
      display: 'flex', 
      flexDirection: 'column',
      gap: 2,
      borderBottom: 1,
      borderColor: 'divider'
    }}>
      <Typography 
        variant="h6" 
        align="center" 
        sx={{ 
          fontWeight: 500,
          color: isToday(currentDate) ? 'primary.main' : 'text.primary'
        }}
      >
        {getDateHeader(currentDate)}
      </Typography>
      <ButtonGroup size="small" aria-label="date navigation" fullWidth>
        <Button onClick={handlePrevious} startIcon={<ChevronLeftIcon />}>
          {t('previousDay')}
        </Button>
        <Button onClick={handleToday} startIcon={<TodayIcon />}>
          {t('today')}
        </Button>
        <Button onClick={handleNext} endIcon={<ChevronRightIcon />}>
          {t('nextDay')}
        </Button>
      </ButtonGroup>
    </Box>
  );

  return (
    <>
      <NavigationHeader />
      <List
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
          p: 0,
          '& .MuiListSubheader-root': {
            bgcolor: 'background.paper',
            lineHeight: '40px',
            fontSize: '1rem',
            fontWeight: 500,
          },
        }}
      >
        {activities.length === 0 ? (
          <ListItem>
            <ListItemText
              primary={
                <Typography color="text.secondary" align="center">
                  {t('noActivities')}
                </Typography>
              }
            />
          </ListItem>
        ) : (
          activities
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .map((activity, index) => (
              <React.Fragment key={activity._id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  button
                  onClick={() => navigate(`/activities/${activity.slug}`)}
                  sx={{
                    py: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack spacing={1}>
                        <Typography variant="subtitle1" component="div">
                          {activity.title}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ color: 'text.secondary' }}
                        >
                          <TimeIcon fontSize="small" />
                          <Typography variant="body2">
                            {format(new Date(activity.date), 'HH:mm')}
                          </Typography>
                          <Chip
                            label={activity.category}
                            size="small"
                            color="primary"
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <GroupIcon fontSize="small" />
                            {getActivityStatusChip(activity)}
                          </Box>
                        </Stack>
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => navigate(`/activities/${activity.slug}`)}>
                      <ChevronRightIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))
        )}
      </List>
    </>
  );
};

export default MobileActivityList; 